/**
 * Email Normalization Utilities (Frontend)
 *
 * Handles email domain variations for matching, particularly for Vertiv
 * where emails may be stored as @vertivco.com or @vertiv.com in different systems.
 *
 * This is a frontend port of server/utils/emailNormalization.ts.
 */

/**
 * Get all Vertiv email domain variants for matching
 *
 * Handles the fact that Vertiv emails may be stored as @vertivco.com in one
 * system (e.g., Snowflake) and @vertiv.com in another (e.g., our users table).
 *
 * @param email - Email address to get variants for
 * @returns Array of lowercase email variants (original + alternate domain if Vertiv)
 *
 * @example
 * getVertivEmailVariants('john.doe@vertivco.com')
 * // Returns: ['john.doe@vertivco.com', 'john.doe@vertiv.com']
 *
 * getVertivEmailVariants('john.doe@gmail.com')
 * // Returns: ['john.doe@gmail.com']
 */
export function getVertivEmailVariants(email: string): string[] {
  if (!email || typeof email !== 'string') {
    return [];
  }

  const lower = email.toLowerCase().trim();
  const variants = [lower];

  if (lower.endsWith('@vertivco.com')) {
    variants.push(lower.replace('@vertivco.com', '@vertiv.com'));
  } else if (lower.endsWith('@vertiv.com')) {
    variants.push(lower.replace('@vertiv.com', '@vertivco.com'));
  }

  return variants;
}

/**
 * Check if an email matches any of the provided emails, considering Vertiv domain variants
 *
 * @param emailToCheck - The email to check
 * @param validEmails - Set of valid emails to match against
 * @returns true if the email (or its Vertiv domain variant) is in the valid set
 */
export function isVertivEmailMatch(
  emailToCheck: string,
  validEmails: Set<string>,
): boolean {
  const variants = getVertivEmailVariants(emailToCheck);
  return variants.some(variant => validEmails.has(variant));
}
